import {Trans} from '@lingui/macro';
import React, {ReactNode, useCallback, useMemo} from 'react';

import {HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {OrNull} from '../../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {DropdownBoxCoordinatorProps} from '../../../../../libs/shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {ClientAtom, ClientFunctionAtom} from '../../../editors/expressions/expressionAtomModel';
import {filterAllowedExpressions} from '../../../editors/expressions/expressionInferHelpers';
import {Expression, ExpressionId} from '../../../editors/expressions/expressionModel';
import {isExpressionRef} from '../../../editors/expressions/expressionRefModel';
import {
  DropdownWithExpressionsProps,
  renderDropdownExpressionIcon,
  renderDropdownExpressionLabel,
  SelectedDropdownItem,
} from '../dropdowns/dropdownWithExpressions';
import {MultipleChoiceDropdown} from '../dropdowns/multipleChoiceDropdown';
import {SingleChoiceDropdown} from '../dropdowns/singleChoiceDropdown';
import {
  defaultDropdownWidth,
  GenericMultipleParameterProps,
  GenericSingleParameterProps,
} from './genericParameter';

/*
 * Types.
 */

interface ExpressionParameterProps extends Omit<GenericSingleParameterProps<Expression>, 'onChange'> {
  expressions: ReadonlyArray<Expression>;
  atom?: ClientAtom | ClientFunctionAtom;
  onChange: HandlerOf<ExpressionId>;
  renderButton?: (isOpen: boolean, props: DropdownBoxCoordinatorProps) => ReactNode;
}

interface ExpressionsParameterProps extends Omit<GenericMultipleParameterProps<Expression>, 'onChange'> {
  expressions: ReadonlyArray<Expression>;
  atom?: ClientAtom | ClientFunctionAtom;
  onChange: HandlerOf<ReadonlyArray<ExpressionId> | undefined>;
  expressionFormat?: string;
}

/*
 * Component.
 */

export const ExpressionParameter = ({expressions, atom, onChange, ...rest}: ExpressionParameterProps) => {
  const items = useMemo(() => filterAllowedExpressions(expressions, atom, false), [expressions, atom]);

  const onExpressionChange = useCallback(
    (item: OrNull<SelectedDropdownItem<Expression>>) => {
      if (!item || !isExpressionRef(item)) {
        return;
      }

      onChange(item.expression);
    },
    [onChange],
  );

  return (
    <SingleChoiceDropdown
      {...rest}
      {...commonProps}
      onChange={onExpressionChange}
      items={items}
      placeholder={<Trans id="rule-editor-select-expression">Select a variable</Trans>}
      title={rest.onSelectCreateExpression ? <Trans id="rule-editor-expression-group">Variable</Trans> : null}
    />
  );
};

export const ExpressionsParameter = ({
  expressions,
  atom,
  expressionFormat,
  onChange,
  ...rest
}: ExpressionsParameterProps) => {
  const items = useMemo(
    () => filterAllowedExpressions(expressions, atom, true, expressionFormat),
    [expressions, atom, expressionFormat],
  );

  const onExpressionChange = useCallback(
    (selectedItems: OrNull<ReadonlyArray<SelectedDropdownItem<Expression>>>) => {
      if (!selectedItems) {
        return;
      }

      onChange(selectedItems.filter(isExpressionRef).map((e) => e.expression));
    },
    [onChange],
  );

  return (
    <MultipleChoiceDropdown
      {...rest}
      {...commonProps}
      onChange={onExpressionChange}
      items={items}
      placeholder={<Trans id="rule-editor-select-expressions">Select variables</Trans>}
      title={
        rest.onSelectCreateExpression ? (
          <Trans id="rule-editor-expressions-group">Dynamic variables</Trans>
        ) : null
      }
    />
  );
};

/*
 * Helpers.
 */

const commonProps: Pick<
  DropdownWithExpressionsProps<Expression>,
  'getItemKey' | 'renderItemLabel' | 'renderItemIcon' | 'serializeItemForSearch' | 'width'
> = {
  getItemKey: (item) => String(item.id),
  renderItemLabel: renderDropdownExpressionLabel,
  renderItemIcon: renderDropdownExpressionIcon,
  serializeItemForSearch: (item) => item.name || item.defaultName,
  width: defaultDropdownWidth,
};
